import { clsx } from 'clsx'
import { IconButton } from 'component/Button'
import { useLatestCallback } from 'hook/useLatestCallback'
import { Icon } from 'icon/Icon'
import { FormEvent, KeyboardEvent, ReactNode, useRef, useState } from 'react'
import { styles } from 'util/style'
import css from './Input.module.scss'

type Props = {
  value: string
  onChange(value: string): void
  onEnter?(): void
  onRef?(element: HTMLInputElement | null): void
  disabled?: boolean
  placeholder?: string
  height?: string | number
  upper?: boolean
  autofocus?: boolean
  error?: boolean
  min?: number
  max?: number
  step?: number
  left?: ReactNode
}

export const Input = (props: Props) => {
  const { value, onChange, onEnter, disabled, placeholder, height, upper, autofocus, error, left } = props
  const [focus, setFocus] = useState(false)
  const timeout = useRef<TimeoutId>()
  const ref = useRef<HTMLInputElement>()
  const className = clsx(css.field, upper && css.upper, focus && css.focus, error && !focus && css.error)
  const style = styles({ height })

  const onRef = useLatestCallback((element: HTMLInputElement | null) => {
    ref.current = element ?? undefined
    if (element && autofocus) element.focus()
    if (props.onRef) props.onRef(element)
  })

  const onFocus = useLatestCallback(() => {
    clearTimeout(timeout.current)
    setFocus(true)
  })

  const onBlur = useLatestCallback(() => {
    timeout.current = setTimeout(() => setFocus(false), 10)
  })

  const onKeyDown = useLatestCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') onChange('')
    if (event.key === 'Enter' && onEnter) onEnter()
  })

  const onChangeInner = useLatestCallback((event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    onChange(value)
  })

  const onClear = useLatestCallback(() => {
    onChange('')
    ref.current?.focus()
  })

  return <div className={className} style={style} onFocusCapture={onFocus} onBlurCapture={onBlur}>
    {left}
    <input ref={onRef} className={css.input} type="text" placeholder={placeholder} disabled={disabled}
      value={value} onChange={onChangeInner} onKeyDown={onKeyDown} />
    {value && <IconButton className={css.clear} action={onClear} disabled={disabled} notab>
      <Icon size={16} name="close" secondary />
    </IconButton>}
  </div>
}
